import { useEffect, useRef } from 'react';

/**
 * Custom hook for infinite scroll functionality
 * @param {Object} config - Configuration object with page, pages info
 * @param {Function} setPage - Function to set the next page
 * @param {boolean} loadingMore - Loading state flag
 * @param {number} threshold - Intersection threshold (0-1)
 */
export const useInfiniteScroll = (config, setPage, loadingMore, threshold = 0.1) => {
  const sentinelRef = useRef();
  const loadingRef = useRef(false);

  useEffect(() => {
    // Guard against undefined config
    if (config?.page === undefined || !config?.pages) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !loadingMore && !loadingRef.current) {
          const nextPage = config.page + 1;
          // Check if there are more pages to load
          if (nextPage < config.pages) {
            loadingRef.current = true;
            setPage(nextPage, true); // true flag for append behavior
          }
        }
      },
      { 
        threshold,
        rootMargin: '200px' // Trigger 200px before sentinel enters viewport
      }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [config, config?.page, config?.pages, setPage, loadingMore, threshold]);

  // Reset loading ref when loading completes or page changes
  useEffect(() => {
    if (!loadingMore) {
      loadingRef.current = false;
    }
  }, [loadingMore, config?.page]);

  return { sentinelRef, isLoading: loadingMore };
};
